import React from 'react';
import { useSelector } from 'react-redux';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const WebTokenPage = () => {

    const profile = useSelector(state => state.auth);
    const [open, setOpen] = React.useState(false);
   
    const handleClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setOpen(false);
    };

    const copyToken = () => {
        navigator.clipboard.writeText(profile.profile.api_token);
        setOpen(true);
    };

    const action = (
        <>
            <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={handleClose}
            >
                <CloseIcon fontSize="small" />
            </IconButton>
        </>
    );

    return (
        <Box sx={{ width: '100%', boxSizing: 'border-box', p: 2.5 }}>
            
            {/* Core Card Document Workspace Surface */}
            <Card 
                className="sc--cardhead--details"
                sx={{ 
                    width: '100%',
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E2E8F0',
                    borderRadius: '16px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.02)',
                    overflow: 'hidden'
                }}
            >
                <CardHeader 
                    title={
                        <Typography variant="h6" component="h1" sx={{ fontWeight: 800, color: '#0F172A', fontSize: '16px', m: 0 }}>
                            API Token
                        </Typography>
                    } 
                    sx={{ p: 2.5, backgroundColor: '#FFFFFF' }}
                />
                
                <Divider sx={{ borderColor: '#E2E8F0' }} />
                
                <CardContent sx={{ p: 4, boxSizing: 'border-box' }}>
                    <Stack spacing={2}>
                        
                        {/* Context Explanatory Label */}
                        <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 500, mb: 1, maxWidth: '600px', lineHeight: 1.6 }}>
                            Use this token to authenticate external API calls to your integration framework pipelines. Keep this secret keys secure.
                        </Typography>

                        {/* Interactive Credentials Action Row Layout Wrapper */}
                        <Box 
                            className="sc--form--center" 
                            sx={{ 
                                display: 'flex',
                                alignItems: 'center',
                                width: '100%',
                                maxWidth: '650px',
                                gap: 1.5
                            }}
                        >
                            <TextField 
                                id="standard-basic" 
                                size='small' 
                                fullWidth
                                defaultValue={profile.profile.api_token} 
                                slotProps={{
                                    input: {
                                        readOnly: true,
                                    },
                                }}
                                variant="outlined" 
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '10px',
                                        backgroundColor: '#F8FAFC',
                                        transition: 'all 0.2s ease-in-out',
                                        border: '1px solid #E2E8F0',
                                        height: '42px',
                                        '& fieldset': { border: 'none' },
                                        '&:hover': { borderColor: '#CBD5E1' },
                                        '&.Mui-focused': {
                                            borderColor: '#1976d2',
                                            boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.08)'
                                        }
                                    },
                                    '& input': {
                                        padding: '10px 14px !important',
                                        fontSize: '13.5px',
                                        color: '#334155',
                                        fontWeight: 600,
                                        fontFamily: 'monospace, Consolas'
                                    }
                                }}
                            />
                            
                            <Button 
                                variant="contained" 
                                onClick={copyToken}
                                startIcon={<ContentCopyIcon sx={{ fontSize: '16px !important' }} />}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: '10px',
                                    fontWeight: 700,
                                    fontSize: '13.5px',
                                    height: '42px',
                                    px: 3,
                                    boxShadow: 'none',
                                    backgroundColor: '#1976d2', // --- CORPORATE BLUE BRAND KEY TOKEN ---
                                    color: '#FFFFFF',
                                    whiteSpace: 'nowrap',
                                    '&:hover': { backgroundColor: '#115293', boxShadow: 'none' }
                                }}
                            >
                                Copy Token
                            </Button>
                        </Box>
                    </Stack>

                    {/* Notification Clipboard Confirmation Popup feedback */}
                    <Snackbar
                        open={open}
                        autoHideDuration={6000}
                        onClose={handleClose}
                        anchorOrigin={{
                            vertical: "top",
                            horizontal: "right"
                        }}
                    >
                        <Alert
                            severity="success"
                            variant="filled"
                            onClose={handleClose}
                            sx={{ 
                                borderRadius: '12px',
                                fontWeight: 600,
                                boxShadow: '0 10px 15px -3px rgba(0,0,0,0.05)',
                                fontSize: '13px',
                                alignItems: 'center'
                            }}
                        >
                            API Token has copied to the clipboard
                        </Alert>
                    </Snackbar>
                </CardContent>
            </Card>
        </Box>
    );
};

export default WebTokenPage;